<template>
  <li
    class="
      tw-group
      tw-flex
      tw-flex-row
      tw-items-center
      tw-justify-between
      tw-select-none
      tw-text-sm
      tw-leading-4
      tw-transition-all
      tw-duration-300
      tw-min-w-0
    "
    :class="containerClasses"
    v-bind="modifiedAttrs"
    :data-testid="`sf-list-item-${props.itemId}`"
    @click.prevent.stop="handleClick"
  >
    <div
      class="
        tw-flex
        tw-flex-auto
        tw-flex-row
        tw-items-center
        tw-justify-start
        tw-justify-self-start
        tw-min-w-0
      "
    >
      <a
        href="#"
        :class="linkClasses"
        data-testid="sf-item-left"
        @click.prevent.stop="handleLeftClick"
      >
        <div
          class="
            tw-flex tw-items-center tw-justify-center tw-text-sm tw-leading-4
            empty:tw-hidden
          "
          :class="[iconMarginClass, props.iconExtraClasses]"
        >
          <slot name="icon" />
        </div>
        <span :class="[labelWrapperClass, props.contentExtraClasses]">
          <slot />
        </span>
      </a>
    </div>
    <div
      class="
        tw-flex tw-flex-row tw-items-center tw-justify-start tw-justify-self-end
      "
    >
      <a
        href="#"
        :class="rightLinkClasses"
        data-testid="sf-item-right"
        @click.prevent.stop="handleRightClick"
      >
        <slot name="right-content" />
      </a>
    </div>
  </li>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from 'vue'

import type { ItemVariants } from './constants'
import { itemVariant } from './constants'

defineOptions({
  name: 'SfListItem',
})

interface Props {
  cornered?: boolean
  selected?: boolean
  variant?: ItemVariants
  background?: 'default' | 'transparent'
  fontWeight?: 'normal' | 'medium' | 'bold'
  itemId: string
  defaultCursor?: boolean
  extraClasses?: string[]
  iconExtraClasses?: string[]
  contentExtraClasses?: string[]
}

type ClickEvent = {
  itemId: string
  side?: 'left' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  variant: itemVariant.xlg,
  background: 'default',
  fontWeight: 'normal',
  defaultCursor: false,
  extraClasses: () => [],
  iconExtraClasses: () => [],
  contentExtraClasses: () => [],
})

const emit =
  defineEmits<{
    'item-click': [payload: ClickEvent]
  }>()

const $attrs = useAttrs()

const isDefaultBackground = computed(() => props.background === 'default')

type VariantClasses = {
  padding: string
  minHeight: string
  labelWrapper: string
  iconMargin: string
}

const variantClasses = {
  [itemVariant.xlg]: {
    padding: 'tw-p-2',
    minHeight: '',
    labelWrapper: 'tw-block tw-py-1',
    iconMargin: 'tw-mr-1',
  },
  [itemVariant.lg]: {
    padding: 'tw-py-1.5 tw-px-2',
    minHeight: 'tw-min-h-[32px]',
    labelWrapper: 'tw-block tw-leading-5',
    iconMargin: 'tw-mr-1.5',
  },
  [itemVariant.md]: {
    padding: 'tw-py-1.5 tw-px-2',
    minHeight: 'tw-min-h-[28px]',
    labelWrapper: 'tw-block tw-leading-4 tw-text-[13px]',
    iconMargin: 'tw-mr-1.5',
  },
} as const satisfies Record<ItemVariants, VariantClasses>

const currentVariantClasses = computed(() => variantClasses[props.variant])

const labelWrapperClass = computed(
  () => currentVariantClasses.value.labelWrapper
)

const iconMarginClass = computed(() => currentVariantClasses.value.iconMargin)

const containerClasses = computed(() => {
  return [
    currentVariantClasses.value.padding,
    currentVariantClasses.value.minHeight,
    {
      'tw-rounded': props.cornered,
      'tw-border-0': props.cornered,
      '[&+&]:tw-mt-0.5': props.cornered,
      'tw-border-b': !props.cornered,
      'tw-border-sf-slate-lighter-1': !props.cornered,
      'last:tw-border-b-0': !props.cornered,
      'tw-bg-sf-snow-lighter': isDefaultBackground.value && !props.selected,
      'tw-bg-sf-primary-subtle':
        props.selected && props.background === 'default',
      'tw-text-sf-snow-lighter':
        props.selected && props.background === 'transparent',
      'tw-bg-sf-primary': props.selected && props.background === 'transparent',
      'hover:tw-bg-sf-primary-darker':
        props.selected && props.background === 'transparent',
      'hover:tw-bg-sf-slate-subtle-2': !props.selected,
      'tw-text-sf-slate': isDefaultBackground.value && !props.selected,
      'hover:tw-text-sf-slate-dark-1':
        isDefaultBackground.value && !props.selected,
      'tw-text-sf-slate-dark-2': isDefaultBackground.value && props.selected,
      'tw-text-sf-neutral-darker-1':
        !isDefaultBackground.value && !props.selected,
      'tw-bg-transparent':
        !props.selected && props.background === 'transparent',
      [`tw-font-${props.fontWeight}`]: !!props.fontWeight,
    },
    ...props.extraClasses,
  ]
})

const linkBaseClasses = computed(() => {
  return {
    'tw-flex tw-flex-row tw-items-center tw-justify-start': true,
    'tw-text-inherit tw-no-underline': true,
    'hover:tw-text-inherit hover:tw-no-underline': true,
    'focus:tw-text-inherit focus:tw-no-underline': true,
    'active:tw-text-inherit active:tw-no-underline': true,
    'tw-cursor-pointer': !props.defaultCursor,
    '!tw-cursor-default': props.defaultCursor,
  }
})

const linkClasses = computed(() => {
  return {
    ...linkBaseClasses.value,
    'tw-grow': true,
    'tw-min-w-0': true,
  }
})

const rightLinkClasses = computed(() => linkBaseClasses.value)

const modifiedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onClick, ...rest } = $attrs
  return rest
})

const handleClick = () => {
  emit('item-click', { itemId: props.itemId })
}

const handleLeftClick = () => {
  emit('item-click', { itemId: props.itemId, side: 'left' })
}

const handleRightClick = () => {
  emit('item-click', { itemId: props.itemId, side: 'right' })
}
</script>
